import AppContext from 'app/AppContext';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import PositionEntryWidgetInputOverlay from 'positionEntry/PositionEntryWidgetInputOverlay';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import './Blotter.scss';
import './OrderGrid.scss';
import {
    blotterColumnDefinitions,
    getRowClass,
    getRowDataId,
} from './blotterGridOptions';
import {
    BlotterColumn,
    useColumnDefinitions,
    useColumnPortfolio,
} from './columns/ColumnPortfolio';
import buildQuickSearchFunction from 'utils/components/QuickSearch/quickSearchFunction';
import { useDispatchBlotterComponentsSetSelectedRowIdsAction } from './store/components/dispatcher';
import { useBlotterComponentSelectedRowIdsSelector } from './store/components/selectors';

import { useBlotterConfigurationSelector } from './store/configuration/selectors';
import { useBlotterFilteredOrders } from './store/filteredOrders/hooks';
import BlotterToolbar from './toolbar/BlotterToolbar';
import { useDispatchBlotterInitialise } from './store/dispatcher';

import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';

import Order from 'communication/types/order/Order';
import { useSelectFirstBatchComplete } from 'app/store/orders/selectors';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useRefreshGridOnAppContextChange } from 'grid/UseRefreshGridOnAppContextChange';
import { useWidgetConfigState } from 'widgets/store/configuration/hooks';
import { BlotterConfiguration } from 'widgets/blotter/store/configuration/BlotterConfiguration';
import { BlotterContextMenu } from 'widgets/blotter/contextMenu/BlotterContextMenu';
import { useBlotterContextMenu } from 'widgets/blotter/contextMenu/useBlotterContextMenu';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

/**
 * Blotter component
 *
 * Connects a Grid with data and column configuration specifics to the orders
 * blotter.
 * Consumes formats from AppContext.
 */
export default function Blotter() {
    const { widgetKey, height } = useWidgetContext();

    const appContext = useContext(AppContext);

    const dispatchBlotterInitialise = useDispatchBlotterInitialise(widgetKey);
    const configState = useWidgetConfigState<BlotterConfiguration>();

    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    useEffect(() => {
        dispatchBlotterInitialise();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetKey]);

    const { visibleColumns, filters } =
        useBlotterConfigurationSelector(widgetKey);

    const columnPortfolio = useColumnPortfolio();
    const columnDefinitions = useColumnDefinitions();

    const {
        contextMenuReference,
        onCloseContextMenu,
        onContextMenu,
        contextMenuOpen,
    } = useBlotterContextMenu();

    // search
    const [searchText, setSearchText] = useState('');

    const onSearchChange = useCallback((value: string) => {
        setSearchText(value);
    }, []);

    const onClearFilter = useCallback(() => {
        setSearchText('');
    }, []);

    const [gridApi, setApi] = useState<GridApi | undefined>();
    const onGridReady = (api: GridApi) => {
        setApi(api);
    };

    const data = useBlotterFilteredOrders(widgetKey);
    const firstBatchComplete = useSelectFirstBatchComplete();

    const showLoadingOverlay = data.length === 0 && !firstBatchComplete;

    // row selection
    const selectedRowIds = (
        useBlotterComponentSelectedRowIdsSelector(widgetKey) || []
    ).map((id) => id.toString());
    const dispatchBlotterComponentsSetSelectedRowIdsAction =
        useDispatchBlotterComponentsSetSelectedRowIdsAction();

    const onSelectedRowIdsChanged = (rowIds: string[]) => {
        dispatchBlotterComponentsSetSelectedRowIdsAction(
            widgetKey,
            rowIds.map((rowId) => +rowId)
        );
    };

    let filteredRowData = data;

    if (searchText) {
        // Quick search
        const quickSearchFunction = buildQuickSearchFunction(
            columnDefinitions,
            searchText,
            visibleColumns,
            appContext
        );

        filteredRowData = data.filter(quickSearchFunction);
    }

    const visCols = visibleColumns.sort().join(',');
    useEffect(() => {
        // Auto size columns when the visible columns have changed, or we receive more rows
        if (gridApi && filteredRowData.length > 0) {
            gridApi.autoSizeAllColumns();
        }
    }, [visCols, gridApi, filteredRowData.length]);

    const isFilterApplied = filters.length > 0;

    // columns definitions
    const colDefs = useMemo(
        () =>
            blotterColumnDefinitions(
                columnPortfolio,
                visibleColumns,
                appContext
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [visCols, columnPortfolio, appContext, configState]
    );

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        Order,
        BlotterColumn
    >(columnDefinitions, colDefs);

    useRefreshGridOnAppContextChange(
        gridApi,
        columnDefinitions,
        visibleColumns,
        appContext
    );

    return (
        <div className="Blotter" data-widget-id={widgetKey}>
            <BlotterContextMenu
                context={contextMenuReference}
                open={contextMenuOpen}
                onClose={onCloseContextMenu}
            />
            <BlotterToolbar
                blotterId={widgetKey}
                columnPortfolio={columnPortfolio}
                columnDefinitions={columnDefinitions}
                filterApplied={isFilterApplied}
                filteredRowCount={filteredRowData.length}
                onClearFilter={onClearFilter}
                onSearchChange={onSearchChange}
                searchText={searchText}
                height={height}
                gridApi={gridApi}
            />
            <PositionEntryWidgetInputOverlay />
            <Grid
                className={'OrderGrid'}
                columnDefs={columnDefs}
                showLoadingOverlay={showLoadingOverlay}
                loadingMessage={'Fetching data...'}
                loadingSpinner={true}
                rowData={filteredRowData}
                selectedRowIds={selectedRowIds}
                suppressColumnVirtualisation={false}
                getRowDataId={getRowDataId}
                getRowClass={getRowClass}
                onGridReady={onGridReady}
                onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                debounceVerticalScrollbar={true}
                onContextMenu={onContextMenu}
                {...gridPropsFromState}
                {...useWidgetEditedColumnNamesProps}
            />
        </div>
    );
}
